/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import ReactPlayer from 'react-player/lazy'
import { findOneVideoCategory_findOneVideoCategory as IVideoCategory } from '__generatedTypes__/findOneVideoCategory'
import { findOneVideo_findOneVideo as IVideo } from '__generatedTypes__/findOneVideo'
import { MembershipType, SubjectEnumType } from '__generatedTypes__/globalTypes'
import DetailsBlock from 'components/DetailsBlock'
import { InputField } from 'components/Input'
import { CourseName } from 'theme/utils'
import { validateRequired } from 'utils/validation'
import { checkPlaylistLink, videoPlayerUrl } from 'utils/videoPlayerUrl'
import SelectMembershipType from 'components/SelectMembershipType/SelectMembershipType'
import { FreeSoloAutocompleteField } from 'components/FreeSoloAutocompleteField/FreeSoloAutocompleteField'
import * as commonClasses from '../commonStyles'
import { IVideoData } from '../types'

export const YOUTUBE_ORIGIN_WWW = 'https://www.youtube.com'
export const YOUTUBE_ORIGIN = 'https://youtube.com'
export const VIMEO_ORIGIN_WWW = 'https://www.vimeo.com'
export const VIMEO_ORIGIN = 'https://vimeo.com'

const ALLOWED_ORIGINS = [YOUTUBE_ORIGIN_WWW, YOUTUBE_ORIGIN, VIMEO_ORIGIN_WWW, VIMEO_ORIGIN]

const isAllowedVideoLink = (value: string) => {
  try {
    return value.startsWith('https://') && ALLOWED_ORIGINS.includes(new URL(value).origin)
  } catch {
    return false
  }
}

const playlistLinkMessage = (singleVideoUrl?: string) =>
  singleVideoUrl
    ? `Playlist links aren't supported. Use the single-video link instead: ${singleVideoUrl}`
    : "Playlist links aren't supported. Paste a link to a single YouTube video."

const validateVideoLink = (value?: string) => {
  if (!value) {
    return validateRequired(value)
  }

  const { isPlaylist, singleVideoUrl } = checkPlaylistLink(value)

  if (isPlaylist) {
    return playlistLinkMessage(singleVideoUrl)
  }

  if (isAllowedVideoLink(value)) {
    return videoPlayerUrl(value) ? undefined : 'Link must point to a single YouTube video'
  }

  return 'Only YouTube or Vimeo videos starting with https:// are allowed'
}

const playlistErrorProps = (link?: string) => {
  const { isPlaylist, singleVideoUrl } = checkPlaylistLink(link)

  return isPlaylist ? { error: true, formHelperText: playlistLinkMessage(singleVideoUrl) } : {}
}

const VideoLinkPreview: FC<{
  link: string
  onDuration: (seconds: number) => void
  onPending: () => void
  onReady: () => void
}> = ({ link, onDuration, onPending, onReady }) => {
  const url = videoPlayerUrl(link)

  useEffect(() => {
    onPending()
  }, [url, onPending])

  if (!url) {
    return null
  }

  return (
    <div css={commonClasses.videoPlayer}>
      <ReactPlayer key={url} onDuration={onDuration} onReady={onReady} url={url} controls width="100%" height="100%" />
    </div>
  )
}

interface IProps {
  videoCategory: IVideoCategory
  initialValues?: Partial<
    Omit<IVideo, 'membershipLevel' | '__typename'> & { membershipLevel: { id: MembershipType | null } }
  >
  handleSubmit: (data: IVideoData, videoLength: number) => void
  onDeleteClick?: () => void
}

const VideoContentForm: FC<IProps> = ({ videoCategory, initialValues, handleSubmit, onDeleteClick }) => {
  const [videoLengthSec, setVideoLengthSec] = useState<number>(0)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const markPreviewPending = useCallback(() => {
    setIsSubmitDisabled(true)
    setVideoLengthSec(0)
  }, [])
  const markPreviewReady = useCallback(() => setIsSubmitDisabled(false), [])

  return (
    <div css={[commonClasses.formContainer, commonClasses.formContainerPadding]}>
      <Typography variant="title3" css={commonClasses.title}>
        {initialValues?.id ? 'Edit Video' : 'Create Video'}
      </Typography>
      <Form
        onSubmit={(values) => handleSubmit(values as IVideoData, Math.round(videoLengthSec))}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} css={commonClasses.contentContainer}>
            <div css={commonClasses.block}>
              <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />
              <InputField
                name="link"
                validate={validateVideoLink}
                label="Video URL"
                placeholder="Enter Video URL"
                fullWidth
                {...playlistErrorProps(values.link)}
              />
              {!!values.link && !checkPlaylistLink(values.link).isPlaylist && (
                <VideoLinkPreview
                  link={values.link}
                  onDuration={setVideoLengthSec}
                  onPending={markPreviewPending}
                  onReady={markPreviewReady}
                />
              )}
              <FreeSoloAutocompleteField
                name="tags"
                label="Video Tags"
                placeholder="Enter Video Tag and hit Enter"
                fullWidth
              />
              <SelectMembershipType minMembershipLevel={videoCategory.membershipLevel} />
            </div>
            <DetailsBlock
              itemName="Video"
              title="Video Details"
              tag={
                videoCategory.subject.type === SubjectEnumType.VideosOnly
                  ? CourseName.CARS
                  : (videoCategory.subject.tabName as CourseName)
              }
              tagLabel={videoCategory.subject.tabName}
              createDate={initialValues?.createdAt}
              statusLive={!!initialValues?.isLive}
              onDeleteClick={onDeleteClick}
              isPublishDisabled={isSubmitDisabled || checkPlaylistLink(values.link).isPlaylist}
              deleteDisabledReason={
                initialValues?.isLive ? "You can't delete Video because Live is enabled" : undefined
              }
            />
          </form>
        )}
      />
    </div>
  )
}
export default VideoContentForm
